/**
 * <load-more-products>
 *
 * Collection "Load more" button. Replaces the numbered pagination: clicking the button fetches the next page of the
 * collection section and appends its product cards to the current <product-list>, then points the button at the page
 * after that. When there are no more pages the button removes itself.
 *
 * The inner <a> href is the single source of truth for "the next page" and works as a no-JS fallback (a normal link
 * to page 2). See snippets/pagination-load-more.liquid.
 */
(() => {
  class LoadMoreProducts extends HTMLElement {
    connectedCallback() {
      this.link = this.querySelector('a');
      if (!this.link) return;
      this.link.addEventListener('click', (event) => this.onClick(event));
    }

    async onClick(event) {
      event.preventDefault();
      if (this.loading || !this.link) return;

      this.loading = true;
      this.link.setAttribute('aria-busy', 'true');

      try {
        const fetchUrl = new URL(this.link.href, window.location.origin);
        fetchUrl.searchParams.set('section_id', this.dataset.sectionId);

        const response = await fetch(fetchUrl.toString());
        if (!response.ok) throw new Error(response.statusText);

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const newList = doc.getElementById(this.dataset.target);
        const currentList = document.getElementById(this.dataset.target);

        if (newList && currentList) {
          const fragment = document.createDocumentFragment();
          Array.from(newList.children).forEach((child) => {
            // The list already ran its scroll-reveal, so appended cards would stay at opacity:0.
            // Drop the attribute so they show immediately.
            if (child.hasAttribute('reveal-on-scroll')) child.removeAttribute('reveal-on-scroll');
            child.querySelectorAll('[reveal-on-scroll]').forEach((el) => el.removeAttribute('reveal-on-scroll'));
            fragment.appendChild(child);
          });
          currentList.appendChild(fragment);
        }

        const nextHref = doc.querySelector('load-more-products a')?.getAttribute('href');
        if (nextHref) {
          this.link.setAttribute('href', nextHref);
        } else {
          this.remove();
        }
      } catch (error) {
        // Something went wrong with the partial fetch — fall back to a full navigation to the next page.
        window.location.assign(this.link.href);
      } finally {
        this.loading = false;
        this.link?.removeAttribute('aria-busy');
      }
    }
  }

  if (!window.customElements.get('load-more-products')) {
    window.customElements.define('load-more-products', LoadMoreProducts);
  }
})();
